/**
 * Данные кейсов — один источник для секции Cases на главной и страницы /keysy.
 * Вынесены из Cases.tsx без правки текста: ru + en как были.
 *
 * NDA: ИИ-кейсы — только сектор + масштаб. Именованные кейсы до эпохи ИИ
 * опубликованы с разрешениями. Ссылки на видео собираются от TG_CASES_URL,
 * чтобы адрес канала жил в одном месте (src/lib/contacts.ts).
 */

import type { Locale } from '@/i18n/locale'
import { TG_CASES_URL } from '@/lib/contacts'

export type CaseCopy = {
  name: string
  tag: string
  metrics: string[]
  trendLabel?: string
  details?: {
    period: string
    approach: string
    steps: string[]
  }
}

export type Case = {
  id: string
  /** Траектория роста (нормализация внутри Sparkline) */
  trendPoints?: number[]
  /** Ссылка на пост с видео-отзывом (TG-канал Антона, RU) */
  videoUrl?: string
} & Record<Locale, CaseCopy>

export const PRE_AI: Case[] = [
  {
    id: 'novavi',
    trendPoints: [90, 110, 145, 180, 230, 300],
    videoUrl: `${TG_CASES_URL}/8`,
    ru: {
      name: 'NovaVi',
      tag: 'Образование и гейминг',
      metrics: [
        'Выручка: $90K → $300K/мес (×3,3)',
        'Прибыль: $20K → $110K/мес (×5,5)',
        '1,5 года · комплексная система управления',
      ],
      trendLabel: 'Рост выручки $90K → $300K/мес',
      details: {
        period: '1,5 года',
        approach:
          'Собирали управленческую вертикаль с нуля: оргсхема, KPI, регулярный менеджмент. Продуктовая воронка переписана под экономику удержания клиентов.',
        steps: [
          'Реструктурировали команду из 15 → 40 человек по функциональным зонам',
          'Внедрили недельный цикл: планёрки, недельные цели, отчётность',
          'Переписали юнит-экономику — вышли на 35 % маржинальность',
          'Запустили внутренний образовательный продукт, превративший расход в прибыль',
        ],
      },
    },
    en: {
      name: 'NovaVi',
      tag: 'Education & gaming',
      metrics: [
        'Revenue: $90K → $300K/mo (×3.3)',
        'Profit: $20K → $110K/mo (×5.5)',
        '1.5 years · end-to-end management system',
      ],
      trendLabel: 'Revenue growth $90K → $300K/mo',
      details: {
        period: '1.5 years',
        approach:
          'Built the management vertical from scratch: org chart, KPIs, regular management cadence. The product funnel was rebuilt around customer-retention economics.',
        steps: [
          'Restructured the team from 15 → 40 people across functional zones',
          'Introduced a weekly cycle: planning meetings, weekly goals, reporting',
          'Rebuilt unit economics — reached 35% margin',
          'Launched an internal education product that turned a cost center into profit',
        ],
      },
    },
  },
  {
    id: 'roofmaster',
    trendPoints: [100, 120, 145, 175, 200],
    videoUrl: `${TG_CASES_URL}/4`,
    ru: {
      name: 'Roofmaster',
      tag: 'Кровельные работы, Латвия',
      metrics: [
        'Выручка ×2, чистая прибыль ×2,5 за год',
        '2 года: выход на рынки Эстонии и Норвегии',
        'Сохранили команду в пандемию · владелец вернулся к семье',
      ],
      trendLabel: 'Выручка ×2 за 2 года',
      details: {
        period: '2 года',
        approach:
          'Вывели владельца из операционки через делегирование и найм сильных заместителей. Построили систему, которая работает без ежедневного участия.',
        steps: [
          'Нашли и внедрили операционного директора',
          'Выстроили найм-систему: от вакансии до адаптации',
          'Прошли пандемию без сокращений — за счёт финансовой подушки и диверсификации',
          'Расширились на 2 новых рынка с локальными партнёрами',
        ],
      },
    },
    en: {
      name: 'Roofmaster',
      tag: 'Roofing services, Latvia',
      metrics: [
        'Revenue ×2, net profit ×2.5 in a year',
        '2 years: expansion into Estonia and Norway',
        'Kept the team through the pandemic · the owner got his family time back',
      ],
      trendLabel: 'Revenue ×2 in 2 years',
      details: {
        period: '2 years',
        approach:
          'Got the owner out of day-to-day operations through delegation and hiring strong deputies. Built a system that runs without his daily involvement.',
        steps: [
          'Found and onboarded a COO',
          'Built a hiring system: from job posting to onboarding',
          'Went through the pandemic with zero layoffs — thanks to a financial cushion and diversification',
          'Expanded into 2 new markets with local partners',
        ],
      },
    },
  },
  {
    id: 'drivemax',
    trendPoints: [25, 28, 32, 36, 40],
    videoUrl: `${TG_CASES_URL}/5`,
    ru: {
      name: 'Drivemax',
      tag: 'Автодилер, USA',
      metrics: [
        'Выручка $25M → $40M за год (×1,6); +30 % — за первые 7 месяцев',
        'Дивиденды +40 % · 20 инструментов управления',
        'Возвращающийся клиент: в 2026 обратился за ИИ-внедрением',
      ],
      trendLabel: 'Выручка $25M → $40M',
      details: {
        period: '1 год',
        approach:
          'Внедрили 20 инструментов управления — от финансового планирования до автоматизации лидогенерации. Поставили регулярную отчётность для владельца.',
        steps: [
          'Финансовое планирование: P&L, cashflow, прогнозы на квартал',
          'Система KPI по всем отделам продаж + сервис',
          'Автоматизированная CRM-воронка и скрипты',
          'Дивидендная стратегия — устойчивые 40 % рост выплат владельцу',
        ],
      },
    },
    en: {
      name: 'Drivemax',
      tag: 'Auto dealer, USA',
      metrics: [
        'Revenue $25M → $40M in a year (×1.6); +30% in the first 7 months',
        'Dividends +40% · 20 management tools deployed',
        'Returning client: came back in 2026 for AI implementation',
      ],
      trendLabel: 'Revenue $25M → $40M',
      details: {
        period: '1 year',
        approach:
          'Deployed 20 management tools — from financial planning to lead-gen automation. Set up regular owner-level reporting.',
        steps: [
          'Financial planning: P&L, cash flow, quarterly forecasts',
          'KPI system across all sales departments + service',
          'Automated CRM funnel and scripts',
          'Dividend strategy — a steady 40% growth in owner payouts',
        ],
      },
    },
  },
  {
    id: 'marposadkabel',
    trendPoints: [400, 550, 780, 1100, 1500, 2100],
    ru: {
      name: 'Марпосадкабель',
      tag: 'Производство',
      metrics: [
        'Выручка: 400М → 2+ млрд ₽ (×5)',
        'Рентабельность: 7 % → 9 %',
        '5 лет · полная управленческая команда сформирована',
      ],
      trendLabel: 'Выручка 400М → 2+ млрд ₽',
      details: {
        period: '5 лет',
        approach:
          'Большая производственная трансформация: от семейного предприятия к управляемому через команду бизнесу с чёткой оргструктурой и сильным финансовым блоком.',
        steps: [
          'Сформирована управленческая команда на ключевых позициях',
          'Внедрён регулярный финменеджмент: бюджетирование, контроллинг',
          'Расширили производство и продуктовую линейку',
          'Рентабельность выросла несмотря на масштабирование ×5',
        ],
      },
    },
    en: {
      name: 'Marposadkabel',
      tag: 'Manufacturing',
      metrics: [
        'Revenue: ₽400M → ₽2B+ (×5)',
        'Profitability: 7% → 9%',
        '5 years · a complete management team built',
      ],
      trendLabel: 'Revenue ₽400M → ₽2B+',
      details: {
        period: '5 years',
        approach:
          'A large-scale manufacturing transformation: from a family-run plant to a team-managed business with a clear org structure and a strong finance function.',
        steps: [
          'Built a management team across key positions',
          'Introduced regular financial management: budgeting, controlling',
          'Expanded production and the product line',
          'Profitability grew despite ×5 scaling',
        ],
      },
    },
  },
  {
    id: 'karate-sushi',
    trendPoints: [10, 22, 40, 65, 100],
    ru: {
      name: 'Карате Суши',
      tag: 'Сеть ресторанов',
      metrics: ['Выручка ×10 за 5 лет', '8 франчайзи в плюсе', 'Франшиза создана с нуля'],
      trendLabel: 'Выручка ×10 за 5 лет',
      details: {
        period: '5 лет',
        approach:
          'Построили франшизную модель: от бренд-бука до операционных стандартов. Каждый из 8 франчайзи вышел в прибыль в первый год.',
        steps: [
          'Разработали операционные стандарты: меню, кухня, сервис',
          'Построили систему обучения управляющих',
          'Юридическая схема франшизы + роялти-модель',
          'Сопровождение каждого франчайзи до точки безубыточности',
        ],
      },
    },
    en: {
      name: 'Karate Sushi',
      tag: 'Restaurant chain',
      metrics: ['Revenue ×10 in 5 years', '8 franchisees in profit', 'Franchise built from scratch'],
      trendLabel: 'Revenue ×10 in 5 years',
      details: {
        period: '5 years',
        approach:
          'Built the franchise model: from brand book to operating standards. Every one of the 8 franchisees became profitable within their first year.',
        steps: [
          'Developed operating standards: menu, kitchen, service',
          'Built a training system for restaurant managers',
          'Franchise legal structure + royalty model',
          'Supported every franchisee to their break-even point',
        ],
      },
    },
  },
  {
    id: 'crypto-exchange',
    trendPoints: [4, 5.2, 6.8, 9],
    ru: {
      name: 'Крипто-биржа',
      tag: 'Финтех · под соглашением о неразглашении',
      metrics: [
        'Чистая прибыль: $4M → $9M/мес (×2,25) за 3,5 месяца',
        '120 сотрудников · оргсхема, KPI, роли внедрены',
        'Отделы подбора и маркетинга запущены с нуля',
      ],
      trendLabel: 'Чистая прибыль $4M → $9M/мес',
      details: {
        period: '3,5 месяца',
        approach:
          'Быстрая хирургия: команда 120 человек без чёткой оргструктуры → управляемая организация с KPI и регулярным менеджментом. Результат в прибыли за квартал.',
        steps: [
          'Оргсхема 120 сотрудников по функциям + иерархия',
          'KPI-система для каждой роли',
          'Собран отдел подбора с нуля — закрывают вакансии за 2 недели',
          'Перестроен маркетинг: от хаотичного к когортному',
        ],
      },
    },
    en: {
      name: 'Crypto Exchange',
      tag: 'Fintech · under NDA',
      metrics: [
        'Net profit: $4M → $9M/mo (×2.25) in 3.5 months',
        '120 employees · org chart, KPIs, roles deployed',
        'Recruiting and marketing departments built from zero',
      ],
      trendLabel: 'Net profit $4M → $9M/mo',
      details: {
        period: '3.5 months',
        approach:
          'Rapid surgery: a 120-person team with no clear org structure → a managed organization with KPIs and regular management. Profit impact within a quarter.',
        steps: [
          'Org chart for 120 employees by function + hierarchy',
          'A KPI system for every role',
          'Built a recruiting department from zero — vacancies now filled in 2 weeks',
          'Rebuilt marketing: from chaotic to cohort-based',
        ],
      },
    },
  },
]

/**
 * ИИ-проекты в работе сейчас. Все под NDA — раскрываем только сектор + масштаб
 * (имена/детали не публикуем, см. products-insight аудит 2026-06-08).
 * Честно помечены как активные engagements, а не «результаты ×N» — ROI-цифр
 * пока нет, выдумывать нельзя. Сила блока — в демонстрации реального спроса
 * на уровне холдингов.
 */
export type AiCaseCopy = {
  sector: string
  scope: string
  facts: string[]
  status: string
}

export type AiCase = {
  id: string
} & Record<Locale, AiCaseCopy>

export const AI_NOW: AiCase[] = [
  {
    id: 'eng-holding',
    ru: {
      sector: 'Проектно-инжиниринговый холдинг · Казахстан',
      scope: 'Оцифровка 23 лет экспертизы в корпоративное ИИ-ядро',
      facts: [
        'Нормы РК и РУз, решения по объектам, требования франшиз — в едином ядре',
        'Калькуляторы рентабельности проекта и эффективности инженерных решений',
        '6 этапов разработки с приёмкой по чек-листам',
      ],
      status: 'Разработка',
    },
    en: {
      sector: 'Engineering & design holding · Kazakhstan',
      scope: 'Digitizing 23 years of expertise into a corporate AI core',
      facts: [
        'National building codes, project decisions, franchise requirements — in a single core',
        'Project profitability and engineering-efficiency calculators',
        '6 development stages with checklist-based acceptance',
      ],
      status: 'In development',
    },
  },
  {
    id: 'fin-holding',
    ru: {
      sector: 'Финансовый холдинг · международная группа',
      scope: 'Treasury-видимость и освобождение свободной ликвидности',
      facts: [
        '30 юрлиц · 100 счетов · 30 валют · оборот 600 млн+',
        'Консолидация в реальном времени вместо ручного сведения выписок',
        'Видимость простаивающих остатков → размещение и контроль FX',
      ],
      status: 'Discovery → пилот',
    },
    en: {
      sector: 'Financial holding · international group',
      scope: 'Treasury visibility and freeing up idle liquidity',
      facts: [
        '30 legal entities · 100 accounts · 30 currencies · 600M+ turnover',
        'Real-time consolidation instead of manual statement reconciliation',
        'Visibility of idle balances → placement and FX control',
      ],
      status: 'Discovery → pilot',
    },
  },
  {
    id: 'dev-holding',
    ru: {
      sector: 'Девелоперский холдинг',
      scope: 'Внедрение Claude Enterprise в работу руководителей',
      facts: [
        'Контекст ролей: регламенты, алгоритмы решений, данные',
        'Корпоративная организация — контекст остаётся в компании',
        '6 недель, обучение внутреннего оператора под ключ',
      ],
      status: 'Внедрение',
    },
    en: {
      sector: 'Real estate development holding',
      scope: 'Rolling out Claude Enterprise across the executive team',
      facts: [
        'Role context: regulations, decision algorithms, data',
        'Corporate workspace — context stays inside the company',
        '6 weeks, turnkey training of an internal operator',
      ],
      status: 'Implementation',
    },
  },
  {
    id: 'fintech',
    ru: {
      sector: 'Финтех · платёжный сервис',
      scope: 'ИИ в работу руководителей и операционных процессов',
      facts: [
        'Архитектура контекста под роли и процессы',
        'Помощники для рутины: подготовка, аналитика, переписка',
        'Принцип «человек в контуре» для критичных решений',
      ],
      status: 'Внедрение',
    },
    en: {
      sector: 'Fintech · payment service',
      scope: 'AI for executives and operational processes',
      facts: [
        'Context architecture for roles and processes',
        'Assistants for routine work: preparation, analytics, correspondence',
        'Human-in-the-loop principle for critical decisions',
      ],
      status: 'Implementation',
    },
  },
]

/**
 * Id живых ИИ-кейсов — порядок карточек на главной (HOME_CASE_ORDER) и
 * якоря /keysy#<id>. Тексты кейсов — в src/data/case-studies.ts; старый
 * CASES_COPY.*.liveCases ниже карточками больше не читается (только три
 * первых кейса, без заборов).
 */
export const LIVE_CASE_IDS = ['fences', 'cosmetics', 'transport', 'neftegaz-trade'] as const

export const CASES_COPY = {
  ru: {
    kicker: 'Эти результаты — до эпохи ИИ',
    heading: 'ИИ не заменил методологию. ИИ ускорил её в разы.',
    intro:
      'Все кейсы ниже Антон делал руками — без ИИ, через системное управление, личное сопровождение и многомесячную работу. Если без ИИ компании росли в 3–10 раз за 1–5 лет, представьте, что мы делаем сейчас — когда ИИ берёт на себя 80% рутины.',
    aiKicker: 'ИИ-проекты в работе сейчас',
    aiHeading: 'Что мы строим прямо сейчас.',
    aiIntro:
      'Все проекты под NDA — раскрываем сектор и масштаб, детали и цифры результата показываем на созвоне под соглашение. Это реальный спрос на уровне холдингов, а не обещания.',
    liveKicker: 'Живые ИИ-кейсы',
    liveHeading: 'Мы публикуем кейсы с середины внедрения.',
    liveIntro:
      'Потому что показывать только финал — показывать отредактированную реальность. Рынок завален «сэкономили 40%» на третьей неделе. Вот что происходит на самом деле:',
    liveCases: [
      {
        sector: 'Производитель косметики · Wildberries и Ozon, 6 кабинетов · ~45 млн ₽ в месяц',
        facts: [
          'Точка входа: сводной отчётности не было, продажи смотрели два раза в неделю, расходы — раз в месяц. Ответ на вопрос «какая маржа у кабинета за период» занимал от дня до недели через финансиста, а все ключевые решения шли через собственника, который управляет удалённо.',
          'За 6 недель: ИИ-контур на восемь человек — от собственника до менеджеров кабинетов, прямые подключения к кабинетам Wildberries и Ozon, учётной системе (только чтение), трекеру, почте и диску. В плане было 3 автоматизации, закрыто 26, ещё 4 в работе. Каждую сотрудник запускал своими руками и защищал перед операционным директором — приёмка внутри компании, а не у подрядчика.',
          'Собственнику каждое утро в 6:00 приходит сводка по шести кабинетам: выручка, прибыль, маржа, доля рекламы в выручке, товары в минусе, кампании, которые сливают бюджет, остатки на исходе.',
          'Впервые увидели: собственный бренд числился в отчёте по рынку конкурентом — доля в нише 12,9 % вместо 37,8 %; весь рост выручки за год (+27 %) — это цена, в штуках роста нет; маржа одной площадки за год упала вдвое; декларации соответствия на 43 % выручки истекают в один день; ключ доступа к финансам всех кабинетов лежал открытым текстом в документе с общей ссылкой — отозван.',
          'Время: команда высвобождает ≈270 часов в месяц — 114–120 тыс. ₽ по окладам. Найдены повторяющиеся потери на ≈200 тыс. ₽ в месяц. Окупаемость — около трёх месяцев только на времени людей, без учёта роста продаж.',
          'Сентябрь к июню, в день: выручка +49 %, прибыль +20 %, рубль рекламы приносит 14,5 ₽ выручки вместо 9,4 — при том что комиссия площадок выросла на 11 процентных пунктов. Рост выручки ИИ не приписываем: это сезон, поставки и цены. Решения по-прежнему принимают люди — контур дал им скорость и полноту картины.',
        ],
        lesson: 'Честно, что не закрыто: найденные потери пока не остановлены — сигнал приходит, действия нет. Следующий шаг — контур исполнения: у каждой находки задача, ответственный, срок и проверка. Повторный замер — через три месяца.',
        quote: { text: '«Получил свыше ожидаемого». «Этот контур является мозгом компании».', who: 'собственник' },
        source: { href: 'https://teletype.in/@batyaro/ainativeseller', label: 'Полный разбор с цифрами и методикой замера — на Teletype' },
      },
      {
        sector: 'Аренда авто для такси · парк ~400 машин · 6 городов',
        facts: [
          'Выручка не выросла ни на рубль: в аренде она упирается в число машин, а закупку собственник сознательно остановил — ремонт дорожал быстрее, чем росла прибыль. Поэтому работали с расходами.',
          'Ремонт — вторая статья расходов после лизинга. Технический директор сам завёл историю ремонта каждой машины и гарантийный свод по запчастям: бюджет ремонта −25–30 %, доля парка в ремонте 14 % → 5,7 %, среднее время ремонта −65 %. Только за июль срезано около 1,15 млн ₽ работ, которые сервис заявил к оплате.',
          'Фонд оплаты труда: 2,5 → 2,0 млн ₽ в месяц — минус 500 тыс. ₽ ежемесячно, 6 млн ₽ в год. Функции ушли в мобильное приложение, которое команда написала сама: около 70 % вопросов водителей закрывается без человека.',
          'Внедрение обошлось дешевле одного месяца этой экономии. Цифры — со слов руководителей на отчётной встрече, с записью. Прибыль по отчёту финансовый директор считает неверной мерой эффекта, поэтому её не публикуем.',
        ],
        lesson: 'Самое дорогое, что находит корпоративный ИИ в первые месяцы, — не автоматизации, а течи и ошибки в ваших данных: повторные ремонты, которые никто не сверял, и гарантийные детали, оплаченные дважды. Что не получилось: кузовной ремонт не подешевел.',
        quote: { text: '«Проблемы теперь решаются на уровне причины, а не последствий».', who: 'собственник' },
        source: { href: 'https://teletype.in/@batyaro/jezbZ6zwQmz', label: 'Полный разбор с цифрами — на Teletype' },
      },
      {
        sector: 'Производственно-торговая компания · 3 направления · нефтегаз',
        facts: [
          'Точка входа: учёт «для налогов», 1С на домашнем компьютере, база в CRM мёртвым грузом.',
          'За 7 рабочих сессий (~3,5 недели): управленческий учёт собран и сверен бухгалтером — «состыковалось полностью»; 1С перенесена на защищённый сервер силами ИИ; клиентская база превращена в дашборд с ИИ в роли руководителя отдела продаж; запущен мониторинг тендеров.',
          'Роли собственника и менеджера верифицированы на 5 из 5. Дашборд собрал сотрудник клиента сам за один рабочий день.',
          'Подрядчики за обслуживание учёта запрашивали 20 000 ₽/мес или 2 000–3 000 ₽/час — функция закрыта внутри контура, покупать не пришлось.',
        ],
        lesson: 'Честно, что ещё не закрыто: высвобождение времени собственника требует его решения о приоритизации направлений — оно впереди. Внедрение продолжается.',
      },
    ],
    liveDocFact: 'Каждое внедрение задокументировано посессионно: трекер, критерии приёмки, статусы. Мы показываем клиенту не презентации, а журнал работ.',
    liveBackground: '65 проектов масштабирования бизнеса до эпохи ИИ. Методология проверена руками — ИИ ускорил её в разы.',
    swipeHint: 'Листайте карточки вбок',
    srPreAi: 'Кейсы масштабирования до эпохи ИИ',
    srLive: 'Живые ИИ-кейсы',
    srAiNow: 'ИИ-проекты в работе сейчас',
    prev: 'Предыдущие карточки',
    next: 'Следующие карточки',
  },
  en: {
    kicker: 'These results — before the AI era',
    heading: 'AI didn’t replace the methodology. AI made it many times faster.',
    intro:
      'Anton delivered every case below by hand — no AI, through systematic management, personal involvement and months of work. If companies grew 3–10× in 1–5 years without AI, imagine what we do now — when AI takes over 80% of the routine.',
    aiKicker: 'AI projects in progress right now',
    aiHeading: 'What we are building right now.',
    aiIntro:
      'All projects are under NDA — we disclose sector and scale; details and result figures are shared on a call under an agreement. This is real demand at the holding level, not promises.',
    liveKicker: 'Live AI cases',
    liveHeading: 'We publish cases from the middle of the implementation.',
    liveIntro:
      'Because showing only the finale is showing an edited reality. The market is flooded with “saved 40%” by week three. Here is what actually happens:',
    liveCases: [
      {
        sector: 'Cosmetics manufacturer · Wildberries & Ozon, 6 seller accounts · ~₽45M a month',
        facts: [
          'Entry point: no consolidated reporting; sales were checked twice a week, costs once a month. Answering “what is this account’s margin for the period” took a day to a week through the finance person, and every key decision went through the owner, who runs the company remotely.',
          'In 6 weeks: an AI perimeter for eight people — from the owner to the account managers, direct connections to the Wildberries and Ozon accounts, the accounting system (read-only), the task tracker, mail and drive. 3 automations were planned; 26 are done and 4 more are in progress. Each employee launched every automation with their own hands and defended it in front of the operations director — acceptance happens inside the company, not at the contractor.',
          'Every morning at 6:00 the owner gets a summary across all six accounts: revenue, profit, margin, ad share of revenue, loss-making products, campaigns burning the budget, stock running low.',
          'Seen for the first time: the company’s own brand was listed as a competitor in the market report — niche share 12.9% instead of 37.8%; the whole year’s revenue growth (+27%) is price, unit sales did not grow; one marketplace’s margin halved in a year; conformity declarations covering 43% of revenue expire on the same day; the finance access key for all accounts sat in plain text in a publicly shared document — revoked.',
          'Time: the team frees ≈270 hours a month — ₽114–120K at base salaries. Recurring losses of ≈₽200K a month found. Payback is about three months on people’s time alone, not counting sales growth.',
          'September vs June, per day: revenue +49%, profit +20%, each ruble of advertising brings ₽14.5 of revenue instead of ₽9.4 — while marketplace fees rose by 11 percentage points. We do not credit AI with the revenue growth: that is season, supplies and pricing. People still make the decisions — the perimeter gave them speed and the full picture.',
        ],
        lesson: 'Honestly, what isn’t closed yet: the losses found are not stopped yet — the signal arrives, the action doesn’t. The next step is an execution loop: every finding gets a task, an owner, a deadline and a check. The repeat measurement is in three months.',
        quote: { text: '“I got more than I expected.” “This perimeter is the company’s brain.”', who: 'the owner' },
        source: { href: 'https://teletype.in/@batyaro/ainativeseller', label: 'Full breakdown with figures and the measurement method — on Teletype (in Russian)' },
      },
      {
        sector: 'Car rental for taxi drivers · fleet of ~400 cars · 6 cities',
        facts: [
          'Revenue did not grow by a single ruble: in rentals it is capped by the number of cars, and the owner deliberately stopped buying new ones — repairs were getting more expensive faster than profit grew. So we worked on costs.',
          'Repairs are the second-largest expense after leasing. The technical director set up a repair history for every car and a warranty register for parts himself: repair budget −25–30%, share of the fleet in repair 14% → 5.7%, average repair time −65%. In July alone about ₽1.15M of work the service had billed was cut.',
          'Payroll: ₽2.5M → ₽2.0M a month — ₽500K less every month, ₽6M a year. The functions moved into a mobile app the team wrote itself: about 70% of drivers’ questions are closed without a human.',
          'The rollout cost less than one month of these savings. Figures are as stated by the company’s executives at a recorded review meeting. The CFO considers reported profit a wrong measure of the effect, so we don’t publish it.',
        ],
        lesson: 'The most valuable thing corporate AI finds in the first months isn’t automations but leaks and errors in your own data: repeat repairs nobody cross-checked, warranty parts paid for twice. What didn’t work: body repairs did not get cheaper.',
        quote: { text: '“Problems are now solved at the level of causes, not consequences.”', who: 'the owner' },
        source: { href: 'https://teletype.in/@batyaro/jezbZ6zwQmz', label: 'Full breakdown with figures — on Teletype (in Russian)' },
      },
      {
        sector: 'Manufacturing & trade company · 3 business lines · oil and gas',
        facts: [
          'Entry point: books kept “for taxes”, the accounting system on a home computer, the CRM base dead weight.',
          'In 7 working sessions (~3.5 weeks): management accounting assembled and checked by the accountant — “everything matched”; the accounting system moved to a secured server by AI; the client base turned into a dashboard with AI as head of sales; tender monitoring launched.',
          'The owner’s and the manager’s roles verified 5 out of 5. The dashboard was built by the client’s employee himself in one working day.',
          'Contractors quoted 20,000 ₽/mo or 2,000–3,000 ₽/hour for accounting support — the function is covered inside the perimeter; nothing had to be bought.',
        ],
        lesson: 'Honestly, what isn’t closed yet: freeing the owner’s time requires his decision on prioritizing the business lines — that’s ahead. The implementation continues.',
      },
    ],
    liveDocFact: 'Every implementation is documented session by session: tracker, acceptance criteria, statuses. We show clients a work journal, not presentations.',
    liveBackground: '65 business scaling projects before the AI era. The methodology was proven by hand — AI made it many times faster.',
    swipeHint: 'Swipe the cards sideways',
    srPreAi: 'Scaling cases before the AI era',
    srLive: 'Live AI cases',
    srAiNow: 'AI projects in progress right now',
    prev: 'Previous cards',
    next: 'Next cards',
  },
}

export const CASE_CARD_COPY = {
  ru: {
    periodPrefix: 'за',
    hide: 'Скрыть детали',
    how: 'Как это сделали',
    periodLabel: 'Срок',
    approachLabel: 'Подход',
    stepsLabel: 'Что сделали',
    video: 'Смотреть видео-отзыв',
    videoAria: (name: string) => `Смотреть видео-отзыв: ${name}`,
  },
  en: {
    periodPrefix: 'in',
    hide: 'Hide details',
    how: 'How we did it',
    periodLabel: 'Timeline',
    approachLabel: 'Approach',
    stepsLabel: 'What we did',
    video: 'Watch video testimonial (RU)',
    videoAria: (name: string) => `Watch video testimonial (in Russian): ${name}`,
  },
}

/**
 * Короткая карточка кейса для ленты на главной: сектор · масштаб · две цифры.
 * Ничего нового — каждая цифра и подпись взяты из metrics / details / facts
 * выше; полный разбор живёт на /keysy#<id>. Ключи — id из PRE_AI и
 * LIVE_CASE_IDS, порядок ленты задаёт HOME_CASE_ORDER.
 */
export type CaseFigure = { value: string; label: string }

export type CaseCardCopy = {
  /** Имя опубликованного кейса или, для NDA, тип компании */
  title: string
  sector: string
  scale: string
  figures: [CaseFigure, CaseFigure]
}

export type HomeCaseId =
  | (typeof LIVE_CASE_IDS)[number]
  | 'novavi' | 'roofmaster' | 'drivemax' | 'marposadkabel' | 'karate-sushi' | 'crypto-exchange'

/**
 * Живые ИИ-кейсы — первыми: страница продаёт ИИ, а они стояли седьмой и
 * восьмой карточками — на десктопе за двумя кликами стрелкой, на телефоне за
 * шестью свайпами (конверсионный аудит 2026-09-21).
 */
export const HOME_CASE_ORDER: HomeCaseId[] = [
  ...LIVE_CASE_IDS,
  'novavi', 'roofmaster', 'drivemax', 'marposadkabel', 'karate-sushi', 'crypto-exchange',
]

export const CASE_CARDS: Record<HomeCaseId, Record<Locale, CaseCardCopy>> = {
  fences: {
    ru: {
      title: 'Производитель заборов и ворот', sector: 'Производство', scale: '~160 млн ₽ в год · 7 бригад · первые 5 недель',
      figures: [
        { value: '636', label: 'цен калькулятора вышли из минуса; для клиента — в среднем +2,4 %' },
        { value: '+24 %', label: 'к прибыли — расчёт на тех же заказах января–сентября' },
      ],
    },
    en: {
      title: 'Fence and gate manufacturer', sector: 'Manufacturing', scale: '~₽160M a year · 7 crews · first 5 weeks',
      figures: [
        { value: '636', label: 'calculator prices out of the red; +2.4% on average for the customer' },
        { value: '+24%', label: 'profit — a calculation on the same January–September orders' },
      ],
    },
  },
  novavi: {
    ru: {
      title: 'NovaVi', sector: 'Образование и гейминг', scale: '1,5 года · команда 15 → 40 человек',
      figures: [
        { value: '×3,3', label: 'выручка: $90K → $300K/мес' },
        { value: '×5,5', label: 'прибыль: $20K → $110K/мес' },
      ],
    },
    en: {
      title: 'NovaVi', sector: 'Education & gaming', scale: '1.5 years · team 15 → 40 people',
      figures: [
        { value: '×3.3', label: 'revenue: $90K → $300K/mo' },
        { value: '×5.5', label: 'profit: $20K → $110K/mo' },
      ],
    },
  },
  roofmaster: {
    ru: {
      title: 'Roofmaster', sector: 'Кровельные работы, Латвия', scale: '2 года · выход в Эстонию и Норвегию',
      figures: [
        { value: '×2', label: 'выручка за год' },
        { value: '×2,5', label: 'чистая прибыль за год' },
      ],
    },
    en: {
      title: 'Roofmaster', sector: 'Roofing services, Latvia', scale: '2 years · expansion into Estonia and Norway',
      figures: [
        { value: '×2', label: 'revenue in a year' },
        { value: '×2.5', label: 'net profit in a year' },
      ],
    },
  },
  drivemax: {
    ru: {
      title: 'Drivemax', sector: 'Автодилер, USA', scale: '1 год · в 2026 вернулся за ИИ-внедрением',
      figures: [
        { value: '×1,6', label: 'выручка за год: $25M → $40M' },
        { value: '+40 %', label: 'дивиденды · 20 инструментов управления' },
      ],
    },
    en: {
      title: 'Drivemax', sector: 'Auto dealer, USA', scale: '1 year · came back in 2026 for AI',
      figures: [
        { value: '×1.6', label: 'revenue in a year: $25M → $40M' },
        { value: '+40%', label: 'dividends · 20 management tools deployed' },
      ],
    },
  },
  marposadkabel: {
    ru: {
      title: 'Марпосадкабель', sector: 'Производство', scale: '5 лет · управленческая команда сформирована',
      figures: [
        { value: '×5', label: 'выручка: 400М → 2+ млрд ₽' },
        { value: '7 → 9 %', label: 'рентабельность' },
      ],
    },
    en: {
      title: 'Marposadkabel', sector: 'Manufacturing', scale: '5 years · a complete management team built',
      figures: [
        { value: '×5', label: 'revenue: ₽400M → ₽2B+' },
        { value: '7 → 9%', label: 'profitability' },
      ],
    },
  },
  'karate-sushi': {
    ru: {
      title: 'Карате Суши', sector: 'Сеть ресторанов', scale: '5 лет · франшиза создана с нуля',
      figures: [
        { value: '×10', label: 'выручка за 5 лет' },
        { value: '8', label: 'франчайзи в плюсе' },
      ],
    },
    en: {
      title: 'Karate Sushi', sector: 'Restaurant chain', scale: '5 years · franchise built from scratch',
      figures: [
        { value: '×10', label: 'revenue in 5 years' },
        { value: '8', label: 'franchisees in profit' },
      ],
    },
  },
  'crypto-exchange': {
    ru: {
      title: 'Крипто-биржа', sector: 'Финтех · под соглашением о неразглашении', scale: '3,5 месяца · 120 сотрудников',
      figures: [
        { value: '×2,25', label: 'чистая прибыль: $4M → $9M/мес' },
        { value: '2 нед', label: 'на вакансию — отдел подбора собран с нуля' },
      ],
    },
    en: {
      title: 'Crypto Exchange', sector: 'Fintech · under NDA', scale: '3.5 months · 120 employees',
      figures: [
        { value: '×2.25', label: 'net profit: $4M → $9M/mo' },
        { value: '2 wk', label: 'per vacancy — recruiting built from zero' },
      ],
    },
  },
  cosmetics: {
    ru: {
      title: 'Производитель косметики', sector: 'Wildberries и Ozon', scale: '~45 млн ₽ в месяц · 6 кабинетов · первые 6 недель',
      figures: [
        { value: '26', label: 'автоматизаций за 6 недель — в плане было 3' },
        { value: '≈3 мес', label: 'окупаемость только на сэкономленном времени команды' },
      ],
    },
    en: {
      title: 'Cosmetics manufacturer', sector: 'Wildberries & Ozon', scale: '~₽45M a month · 6 seller accounts · first 6 weeks',
      figures: [
        { value: '26', label: 'automations in 6 weeks — 3 were planned' },
        { value: '≈3 mo', label: 'payback on saved team time alone' },
      ],
    },
  },
  transport: {
    ru: {
      title: 'Аренда авто для такси', sector: 'Транспорт', scale: 'парк ~400 машин · замер после 5 месяцев',
      figures: [
        { value: '−20 %', label: 'фонд оплаты труда: было 2,5, стало 2,0 млн ₽ в месяц' },
        { value: '5,7 %', label: 'парка в ремонте вместо 14 %; бюджет ремонта −25–30 %' },
      ],
    },
    en: {
      title: 'Car rental for taxis', sector: 'Transportation', scale: 'fleet of ~400 cars · measured after 5 months',
      figures: [
        { value: '−20%', label: 'payroll: from ₽2.5M down to ₽2.0M a month' },
        { value: '5.7%', label: 'of the fleet in repair instead of 14%; repair budget −25–30%' },
      ],
    },
  },
  'neftegaz-trade': {
    ru: {
      title: 'Производство и торговля', sector: 'Нефтегаз', scale: '3 направления · внедрение продолжается',
      figures: [
        { value: '7 сессий', label: '~3,5 недели — управленческий учёт сверен бухгалтером' },
        { value: '5 / 5', label: 'роли собственника и менеджера верифицированы' },
      ],
    },
    en: {
      title: 'Manufacturing & trade', sector: 'Oil and gas', scale: '3 business lines · the implementation continues',
      figures: [
        { value: '7 sessions', label: '~3.5 weeks — management accounting checked by the accountant' },
        { value: '5 / 5', label: 'the owner’s and the manager’s roles verified' },
      ],
    },
  },
}

/** Тексты секции кейсов на главной; заголовок общий с /keysy — CASES_COPY.heading. */
export const CASES_HOME_COPY = {
  ru: {
    eyebrow: 'Кейсы · 4 живых внедрения ИИ + 65 масштабирований бизнеса',
    lede: 'Четыре первых кейса — живые внедрения ИИ, публикуем с середины: показывать только финал — значит показывать отредактированную реальность. Шесть следующих Антон делал руками до эпохи ИИ — системным управлением и месяцами работы: рост в 3–10 раз за 1–5 лет; всего проектов масштабирования — 65.',
    chipPre: 'до эпохи ИИ',
    chipLive: 'живой ИИ-кейс',
    open: 'Разбор →',
    openAria: (name: string) => `Разбор кейса: ${name}`,
    srAll: 'Кейсы: масштабирование до эпохи ИИ и живые внедрения ИИ',
  },
  en: {
    eyebrow: 'Cases · 4 live AI rollouts + 65 business scaling projects',
    lede: 'The first four cases are live AI implementations, published from the middle: showing only the finale is showing an edited reality. The next six Anton delivered by hand before the AI era — systematic management and months of work: growth of 3–10× in 1–5 years; 65 such scaling projects in total.',
    chipPre: 'before the AI era',
    chipLive: 'live AI case',
    open: 'Breakdown →',
    openAria: (name: string) => `Case breakdown: ${name}`,
    srAll: 'Cases: scaling before the AI era and live AI rollouts',
  },
}
